// Die Regeln der Instrumentseite — ohne Netz, damit Seite und Vorlage
// dieselben sind.
//
// Rechts neben den Stammdaten stehen vier Karten: Fälle, News,
// Einschätzungen und Ideen zu diesem Titel. Jede Zeile hat eine Marke,
// einen Titel, eine Unterzeile und die Zeit; ein Klick öffnet das Detail
// in der Schublade rechts. Was aus einem Datensatz eine Zeile und was ein
// Detail macht, steht hier.
import type { Assessment, CaseDetail, NewsItem, PriceHistory, Quote,
              ResearchCase, TradeIdea } from '../api/client'
import { label } from '../pages/researchVocabulary'
import { CLASSIFICATION, STATUS as IDEE_STATUS } from '../pages/tradeIdeaVocabulary'
import { geldtext } from '../pages/priceFormat'
import type { Ton } from './faelleRegeln'
import { wirkungTon } from './faelleRegeln'
import { abstandAus } from './ideenRegeln'

export type Aktivitaet = 'fall' | 'news' | 'ampel' | 'idee'

/** Was die Karten von einem Fall brauchen. `ResearchCase` erfüllt es. */
export type FallKurz = Pick<ResearchCase, 'id' | 'title' | 'status'
  | 'regime_impact' | 'probability' | 'last_material_update_at' | 'opened_at' | 'created'>

/** Wann zuletzt etwas geschah: die Entwicklung, sonst die Eröffnung, sonst die Anlage. */
function fallZeit(f: FallKurz): string {
  return f.last_material_update_at ?? f.opened_at ?? f.created
}
/** Was die Karten von einer Meldung brauchen. `NewsItem` erfüllt es. */
export type NewsKurz = Pick<NewsItem, 'id' | 'headline' | 'body' | 'sentiment'
  | 'published_at' | 'targets' | 'sources'>
/** Was die Karten von einer Einschätzung brauchen. `Assessment` erfüllt es. */
export type AmpelKurz = Pick<Assessment, 'id' | 'rating' | 'headline' | 'note'
  | 'assessed_at' | 'confirmed_at' | 'source'>

/** Eine Zeile einer Aktivitätskarte. */
export interface AktivZeile {
  key: string
  art: Aktivitaet
  id: number
  /** „▲", „−2", „◆" — die Marke links, in Farbe. */
  marke: string
  ton: Ton
  /** Bei News steht die Zahl weiß auf Farbe; sonst Farbe auf hellem Grund. */
  gefuellt: boolean
  titel: string
  sub: string
  at: Date
  /** „heute 08:40", „gest. 17:40", „02.09." */
  zeit: string
  /** „Montag, 7. September 2026, 08:40 Uhr" — beim Zeigen. */
  zeitVoll: string
}

/** Was die Schublade zu einer Zeile zeigt. */
export interface AktivDetail {
  art: Aktivitaet
  id: number
  /** „FALL", „NEWS · HANDELSBLATT", „EINSCHÄTZUNG", „HANDELSIDEE" */
  typ: string
  titel: string
  ton: Ton
  felder: { k: string; v: string; ton?: Ton }[]
  text: string | null
  verlauf: { datum: string; text: string }[]
  /** Der Sprung auf die Seite dahinter. */
  oeffnen: { text: string; to: string }
}

// ---------------------------------------------------------------- Zeit

const UHR = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })
const TAG = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' })
const TAG_JAHR = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
const VOLL = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
})
const ZEITPUNKT = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' })

function tagKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

/** „heute 08:40", „gest. 17:40", „02.09.", bei einem anderen Jahr „02.09.25". */
export function zeitKurz(iso: string, heute: Date = new Date()): string {
  const d = new Date(iso)
  const gestern = new Date(heute); gestern.setDate(heute.getDate() - 1)
  if (tagKey(d) === tagKey(heute)) return `heute ${UHR.format(d)}`
  if (tagKey(d) === tagKey(gestern)) return `gest. ${UHR.format(d)}`
  return d.getFullYear() === heute.getFullYear() ? TAG.format(d) : TAG_JAHR.format(d)
}

/** „Montag, 7. September 2026, 08:40 Uhr" */
export function zeitVoll(iso: string): string { return `${VOLL.format(new Date(iso))} Uhr` }

/** „07.09.2026, 08:40" — für Felder und Verlauf. */
export function zeitpunkt(iso: string): string { return ZEITPUNKT.format(new Date(iso)) }

// --------------------------------------------------------------- Zeilen

function basis(art: Aktivitaet, id: number, iso: string, heute: Date) {
  return { key: `${art}-${id}`, art, id, at: new Date(iso),
           zeit: zeitKurz(iso, heute), zeitVoll: zeitVoll(iso) }
}

function wahrscheinlichkeit(p: string | null): string | null {
  if (p === null) return null
  const n = Number(p)
  if (!Number.isFinite(n)) return null
  return `Wahrsch. ${Math.round(n <= 1 ? n * 100 : n)} %`
}

function wirkung(w: number): string {
  return `Wirkung ${w > 0 ? '+' : w < 0 ? '−' : ''}${Math.abs(w)}`
}

/** Ein Fall: Marke nach Wirkung, Unterzeile Stand, Wirkung, Wahrscheinlichkeit. */
export function zeileAusFall(f: FallKurz, heute: Date = new Date()): AktivZeile {
  const ton = wirkungTon(f.regime_impact)
  return {
    ...basis('fall', f.id, fallZeit(f), heute),
    marke: f.regime_impact > 0 ? '▲' : f.regime_impact < 0 ? '▼' : '●',
    ton, gefuellt: false,
    titel: f.title,
    sub: [label('case_status', f.status), wirkung(f.regime_impact),
          wahrscheinlichkeit(f.probability)].filter(Boolean).join(' · '),
  }
}

/** Eine Meldung: der Score als Zahl auf Farbe, darunter, wen sie betrifft. */
export function zeileAusNews(n: NewsKurz, heute: Date = new Date()): AktivZeile {
  const s = n.sentiment
  const namen = n.targets.map((t) => t.name).filter((x): x is string => Boolean(x))
  return {
    ...basis('news', n.id, n.published_at, heute),
    marke: s > 0 ? `+${s}` : s < 0 ? `−${Math.abs(s)}` : '0',
    ton: s > 0 ? 'good' : s < 0 ? 'bad' : 'muted', gefuellt: true,
    titel: n.headline,
    sub: namen.slice(0, 3).join(' · ') + (namen.length > 3 ? ` +${namen.length - 3}` : '')
      || n.sources[0]?.publisher || 'ohne Bezug',
  }
}

const STUFE: Record<Assessment['rating'], string> = { GREEN: 'grün', AMBER: 'gelb', RED: 'rot' }
const STUFE_TON: Record<Assessment['rating'], Ton> = { GREEN: 'good', AMBER: 'warn', RED: 'bad' }
const STUFE_MARKE: Record<Assessment['rating'], string> = { GREEN: '▲', AMBER: '●', RED: '▼' }

/** Eine Einschätzung: Marke nach Stufe, Unterzeile Stufe und Quelle. */
export function zeileAusAmpel(a: AmpelKurz, heute: Date = new Date()): AktivZeile {
  return {
    ...basis('ampel', a.id, a.assessed_at, heute),
    marke: STUFE_MARKE[a.rating], ton: STUFE_TON[a.rating], gefuellt: false,
    titel: a.headline,
    sub: `Ampel ${STUFE[a.rating]} · ${a.source}`
      + (a.confirmed_at ? ` · bestätigt ${zeitKurz(a.confirmed_at, heute)}` : ''),
  }
}

/** Eine Idee: die Raute, Unterzeile Stand, Einstufung und Abstand. */
export function zeileAusIdee(i: TradeIdea, heute: Date = new Date()): AktivZeile {
  const a = abstandAus(i)
  return {
    ...basis('idee', i.id, i.created, heute),
    marke: '◆', ton: 'accent', gefuellt: false,
    titel: i.headline,
    sub: [IDEE_STATUS[i.status] ?? i.status, CLASSIFICATION[i.classification] ?? i.classification,
          a.abstand !== '—' ? `${a.abstand} ${a.abstandSub}` : a.abstandSub].join(' · '),
  }
}

// --------------------------------------------------------------- Details

const RICHTUNG: Record<string, string> = {
  POSITIVE: '▲', NEGATIVE: '▼', NEUTRAL: '●', UNCLEAR: '?',
}

/**
 * Das Detail eines Falls. `detail` ist das nachgeladene Voll-Detail —
 * fehlt es noch, steht die Zusammenfassung leer und der Verlauf kommt nach.
 */
export function detailAusFall(f: FallKurz, detail: CaseDetail | null): AktivDetail {
  const ton = wirkungTon(f.regime_impact)
  return {
    art: 'fall', id: f.id, typ: 'FALL', titel: f.title, ton,
    felder: [
      { k: 'Stand', v: [label('case_status', f.status), wirkung(f.regime_impact),
                        wahrscheinlichkeit(f.probability)].filter(Boolean).join(' · '), ton },
      { k: 'Zeit', v: zeitpunkt(fallZeit(f)) },
      ...(detail?.next_review_at ? [{ k: 'Wiedervorlage', v: zeitpunkt(detail.next_review_at) }] : []),
    ],
    text: detail?.summary ?? detail?.analyst_assessment ?? null,
    verlauf: (detail?.updates ?? []).map((u) => ({
      datum: zeitpunkt(u.recorded_at),
      text: `${u.direction ? RICHTUNG[u.direction] + ' ' : ''}${u.headline}`,
    })),
    oeffnen: { text: 'Fall öffnen →', to: `/research/${f.id}` },
  }
}

export function detailAusNews(n: NewsKurz, isin: string): AktivDetail {
  const s = n.sentiment
  const ton: Ton = s > 0 ? 'good' : s < 0 ? 'bad' : 'muted'
  const quelle = n.sources[0]?.publisher ?? n.sources[0]?.title ?? null
  return {
    art: 'news', id: n.id, typ: quelle ? `NEWS · ${quelle.toUpperCase()}` : 'NEWS',
    titel: n.headline, ton,
    felder: [
      { k: 'Score', v: `${s > 0 ? '+' : s < 0 ? '−' : ''}${Math.abs(s)} von ±3`, ton },
      { k: 'Betrifft', v: n.targets.map((t) => t.name ?? t.ref).join(' · ') || 'ohne Bezug' },
      { k: 'Zeit', v: zeitpunkt(n.published_at) },
    ],
    text: n.body,
    verlauf: n.sources.map((q) => ({ datum: q.publisher ?? 'Quelle', text: q.title })),
    oeffnen: { text: 'Meldungen öffnen →', to: `/news?ref=${encodeURIComponent(`isin:${isin}`)}` },
  }
}

export function detailAusAmpel(a: AmpelKurz, isin: string): AktivDetail {
  const ton = STUFE_TON[a.rating]
  return {
    art: 'ampel', id: a.id, typ: 'EINSCHÄTZUNG', titel: a.headline, ton,
    felder: [
      { k: 'Ampel', v: STUFE[a.rating], ton },
      { k: 'Von', v: a.source },
      { k: 'Zeit', v: zeitpunkt(a.assessed_at) },
      { k: 'Bestätigt', v: a.confirmed_at ? zeitpunkt(a.confirmed_at) : 'noch nicht' },
    ],
    text: a.note,
    verlauf: [],
    oeffnen: { text: 'Einschätzungen öffnen →',
               to: `/assessments?ref=${encodeURIComponent(`isin:${isin}`)}&von=-&bis=-` },
  }
}

export function detailAusIdee(i: TradeIdea): AktivDetail {
  const a = abstandAus(i)
  return {
    art: 'idee', id: i.id, typ: 'HANDELSIDEE', titel: i.headline, ton: 'accent',
    felder: [
      { k: 'Status', v: IDEE_STATUS[i.status] ?? i.status, ton: i.status === 'ACTIVE' || i.status === 'ENTERED' ? 'good' : 'muted' },
      { k: 'Einordnung', v: CLASSIFICATION[i.classification] ?? i.classification },
      { k: 'Abstand', v: a.abstand !== '—' ? `${a.abstand} · ${a.abstandSub}` : a.abstandSub, ton: a.abstandTon },
      ...(i.entry_low && i.entry_high
        ? [{ k: 'Zone', v: `${geldtext(i.entry_low)} – ${geldtext(i.entry_high)}` }] : []),
      { k: 'Seit', v: zeitpunkt(i.created) },
    ],
    text: i.rationale,
    verlauf: [],
    oeffnen: { text: 'Idee öffnen →', to: `/trade-ideas?q=${encodeURIComponent(i.isin)}` },
  }
}

// ------------------------------------------------------------------ Kurs

/** Der Kurs im Kopf: Betrag, Veränderung zum Vortag, Zeitpunkt. */
export interface KursStand {
  kurs: Quote
  /** Fehlt, wenn kein Schlusskurs des Vortags da ist. */
  aenderung: { absolut: number; prozent: number; ton: Ton } | null
}

/** Wie alt der Vortagsschluss höchstens sein darf — sonst ist er kein Vortag. */
const VORTAG_HOECHSTENS_TAGE = 7

/**
 * Die Veränderung zum letzten Schlusskurs VOR dem Tag des Kurses. Der Kurs
 * ist der jüngste abgelesene, die Balken die letzten Tage der Historie.
 *
 * NUR MIT ECHTEM VORTAG: Liegt der jüngste Schluss der Historie Wochen
 * zurück, ist die Differenz keine Tagesveränderung, sondern ein Sprung über
 * eine Lücke — und stünde als „+155 %" da. Dann lieber nichts.
 */
export function kursStandAus(kurs: Quote | null, balken: PriceHistory[]): KursStand | null {
  if (!kurs) return null
  const tag = kurs.observed_at.slice(0, 10)
  const grenze = new Date(kurs.observed_at)
  grenze.setDate(grenze.getDate() - VORTAG_HOECHSTENS_TAGE)
  const vortag = balken
    .filter((b) => b.trading_date < tag && new Date(b.trading_date) >= grenze)
    .sort((a, b) => (a.trading_date < b.trading_date ? 1 : -1))[0]
  if (!vortag) return { kurs, aenderung: null }
  const jetzt = Number(kurs.price.amount), vorher = Number(vortag.close.amount)
  if (!Number.isFinite(jetzt) || !Number.isFinite(vorher) || vorher === 0) return { kurs, aenderung: null }
  const absolut = jetzt - vorher
  return { kurs, aenderung: { absolut, prozent: absolut / vorher * 100,
                              ton: absolut > 0 ? 'good' : absolut < 0 ? 'bad' : 'muted' } }
}
